/**
 * The recap email's "share with external participants" link.
 *
 * A recap lands in an internal attendee's inbox with a button on it, and this
 * is where the button goes. Opening the page shares NOTHING: it lists the
 * people the emailed token covers, lets the sender take anyone off the list,
 * and sends only once they confirm — so a link scanner that fetches the URL
 * shares nothing.
 *
 * **This is the one surface with no session.** The token in the URL is the
 * whole credential, so every call here goes to `/api/recap/external-share/:token`
 * rather than to the authenticated endpoints the rest of the app uses — the
 * reader has no account and the request carries no bearer token. That is also
 * why the payload is narrow: it is a title, the external invitees and the
 * notes, and nothing else. The meeting's transcript, its space and its internal
 * attendees never reach this page, so a forwarded link cannot widen into them.
 */
import { apiGet, apiSend } from '@/lib/api'
import type { MeetingNote, Person } from './types'

/** The backend's cap on addresses beyond the meeting's own invitees. */
export const MAX_ADDED_RECIPIENTS = 10

export interface ShareRecipient extends Person {
  /** On the meeting invite, as opposed to typed in on this page. */
  onInvite: boolean
  /** Held access before this page was opened, so re-sending is a repeat email. */
  alreadyShared: boolean
}

export interface ExternalShare {
  token: string
  /** Kept so mutations can address the meeting; never rendered. */
  meetingId: string
  title: string
  /** ISO 8601. Which instance of a recurring meeting this recap is for. */
  startsAt: string | null
  /** An organisation can turn email sharing off, and the link then says so. */
  enabled: boolean
  recipients: ShareRecipient[]
  notes: MeetingNote[]
}

export interface ShareResult {
  title: string
  shared: string[]
  failed: string[]
}

/* ---------- The wire ---------- */

interface WireShare {
  meeting_id: string
  meeting_title: string
  meeting_started_at: string | null
  enabled: boolean
  participants: Array<{
    email: string
    name?: string | null
    display_name?: string | null
    is_internal?: boolean
    already_shared: boolean
  }>
  notes: Array<{
    id: string
    title: string
    content_md: string | null
    shared: boolean
  }>
}

function toShare(token: string, wire: WireShare): ExternalShare {
  return {
    token,
    meetingId: wire.meeting_id,
    title: wire.meeting_title,
    startsAt: wire.meeting_started_at,
    enabled: wire.enabled,
    // Everyone the token covers is by definition an external invitee; the
    // endpoint has already excluded the internal attendees.
    recipients: wire.participants.map((entry) => ({
      email: entry.email,
      name: entry.display_name || entry.name || entry.email,
      isInternal: entry.is_internal ?? false,
      onInvite: true,
      alreadyShared: entry.already_shared,
    })),
    notes: wire.notes.map((note) => ({
      id: note.id,
      templateId: null,
      title: note.title,
      body: note.content_md ?? '',
      sharedExternally: note.shared,
      pending: false,
      prompt: null,
    })),
  }
}

export async function loadExternalShare(token: string): Promise<ExternalShare> {
  const wire = await apiGet<WireShare>(`/api/recap/external-share/${encodeURIComponent(token)}`)
  return toShare(token, wire)
}

/**
 * Directory lookup for the address field.
 *
 * Behind the same token as everything else on this page — there is no session
 * to authorise a directory search, so the token is what scopes it. Internal
 * people come back labelled rather than filtered out: the sender knows who they
 * mean, and silently dropping a colleague reads as the search being broken.
 */
export async function searchDirectory(
  token: string,
  query: string,
  exclude: Set<string>,
  limit = 6
): Promise<Person[]> {
  const normalized = query.trim()
  if (normalized.length < 2) return []
  const { people } = await apiGet<{
    people: Array<{ email: string; name: string | null; is_internal: boolean }>
  }>(
    `/api/recap/external-share/${encodeURIComponent(token)}/people?query=${encodeURIComponent(normalized)}`
  )
  return people
    .filter((entry) => !exclude.has(entry.email))
    .map((entry) => ({
      email: entry.email,
      name: entry.name || entry.email,
      isInternal: entry.is_internal,
    }))
    .slice(0, limit)
}

/**
 * Grant the chosen addresses and mail them.
 *
 * One call rather than one per address, because that is what the endpoint
 * behind it is — and because a half-finished loop would leave some people
 * emailed and the page still saying nothing had happened. The server decides
 * which addresses actually went out, so `shared` and `failed` are read off its
 * answer rather than assumed from the request.
 */
export async function shareExternally(
  share: ExternalShare,
  emails: string[],
  language: string
): Promise<ShareResult> {
  const result = await apiSend<{ shared?: string[]; failed?: string[] }>(
    'POST',
    `/api/recap/external-share/${encodeURIComponent(share.token)}`,
    { emails, language }
  )
  return {
    title: share.title,
    shared: result.shared ?? emails,
    failed: result.failed ?? [],
  }
}

/* ---------- Notes ---------- */

/**
 * Include a note in the share, or leave it out.
 *
 * Through the token's own endpoint, not the meeting's: this page has no
 * session, and the note it is toggling belongs to a meeting the reader cannot
 * otherwise address.
 */
export async function setNoteShared(
  share: ExternalShare,
  noteId: string,
  shared: boolean
): Promise<MeetingNote[]> {
  await apiSend(
    'PATCH',
    `/api/recap/external-share/${encodeURIComponent(share.token)}/notes/${noteId}`,
    { shared }
  )
  return (await loadExternalShare(share.token)).notes
}

/**
 * NOT WIRED — the token's endpoints toggle a note's inclusion and nothing else.
 *
 * Editing a note's text from this page would need a write route scoped to the
 * token, and the sender is deciding what to disclose rather than rewriting it.
 * Failing loudly beats a field that silently discards what was typed.
 */
export function setNoteBody(
  _share: ExternalShare,
  _noteId: string,
  _body: string
): Promise<MeetingNote[]> {
  return Promise.reject(
    new Error('Notes cannot be edited from a share link. Open the meeting to change one.')
  )
}

/** NOT WIRED — generating a note needs the meeting, which this token cannot reach. */
export function extractNote(_share: ExternalShare, _templateId: string): Promise<MeetingNote[]> {
  return Promise.reject(
    new Error('New notes have to be written from the meeting itself, not from a share link.')
  )
}
